/**
 * QuizCatalogueSection — the mentor board's quiz shelf.
 *
 * Every runnable training component the lead can reach, whatever its status:
 * `GET /protocols` answers one row per protocol (the version in force, or else
 * the newest), so a quiz that exists only as a draft is listed too.
 *
 * Read-only. A quiz is a protocol graph, authored in the protocol Studio like
 * any other protocol; there is no quiz-shaped form and no quiz codec.
 *
 * Connected, unlike its sibling sections: the shelf spans every status while
 * the board's `components` prop is the published-only set that names a
 * target's modules, and threading a second, differently-filtered list through
 * MentorBoard's props would make every caller carry a query it doesn't use.
 */

import { useTranslation } from 'react-i18next'
import { FileQuestion } from 'lucide-react'

import { Spinner } from '@/components/ui/spinner'
import { EmptyState } from '@/components/clinical'
import { useTrainingComponents } from '@/hooks/useTraining'
import type { ProtocolSummary, ProtocolVersionStatus } from '@/api/endpoints/protocols'

export function QuizCatalogueSection({ className }: { className?: string }) {
  const { t } = useTranslation()
  const quizzes = useTrainingComponents({ composesComponents: false })
  const rows = quizzes.data ?? []

  return (
    <section className={className}>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="flex min-w-0 flex-1 items-center gap-2 text-[15px] font-semibold text-neutral-900">
          <FileQuestion className="h-4 w-4 text-neutral-400" aria-hidden="true" />
          {t('mentor.quiz.sectionTitle', 'Quizzes')}
        </h2>
      </div>

      {/* ERROR */}
      {quizzes.isError && (
        <EmptyState
          icon={<FileQuestion aria-hidden="true" />}
          title={t('mentor.quiz.listErrorTitle', 'Could not load the quizzes')}
          description={t('mentor.quiz.listError', 'The quiz catalogue could not be loaded.')}
          action={{ label: t('actions.retry', 'Retry'), onClick: () => void quizzes.refetch() }}
        />
      )}

      {/* LOADING (no cached data) */}
      {quizzes.isLoading && !quizzes.data && (
        <div className="flex items-center justify-center py-8">
          <Spinner className="h-5 w-5 text-neutral-400" />
          <span className="sr-only">{t('common.loading', 'Loading...')}</span>
        </div>
      )}

      {/* EMPTY */}
      {quizzes.data && rows.length === 0 && (
        <EmptyState
          icon={<FileQuestion aria-hidden="true" />}
          title={t('mentor.quiz.emptyTitle', 'No quizzes yet')}
          description={t(
            'mentor.quiz.empty',
            'Ask the assistant to generate a quiz from one of your unit’s documents, or model one in the protocol Studio.',
          )}
        />
      )}

      {/* DATA */}
      {rows.length > 0 && (
        <ul className="space-y-2">
          {rows.map((q) => (
            <QuizRow key={q.id} quiz={q} />
          ))}
        </ul>
      )}
    </section>
  )
}

function QuizRow({ quiz }: { quiz: ProtocolSummary }) {
  return (
    <li className="rounded-[18px] border border-neutral-200 bg-white p-4">
      <p className="truncate text-[15px] font-semibold text-neutral-900">{quiz.title}</p>
      {quiz.description && (
        <p className="mt-0.5 line-clamp-2 text-[13px] text-neutral-500">{quiz.description}</p>
      )}
      <p className="mt-1 flex flex-wrap items-center gap-2 text-[13px] text-neutral-500">
        <StatusBadge status={quiz.status} />
        <span>{quiz.version}</span>
      </p>
    </li>
  )
}

function StatusBadge({ status }: { status: ProtocolVersionStatus }) {
  const { t } = useTranslation()
  const label = {
    draft: t('mentor.quiz.statusDraft', 'Draft'),
    in_review: t('mentor.quiz.statusInReview', 'In review'),
    published: t('mentor.quiz.statusPublished', 'Published'),
    archived: t('mentor.quiz.statusArchived', 'Archived'),
  }[status]
  const tone = {
    draft: 'border-amber-300 text-amber-800',
    in_review: 'border-amber-300 text-amber-800',
    published: 'border-neutral-300 text-neutral-700',
    archived: 'border-neutral-200 text-neutral-400',
  }[status]
  return <span className={`rounded-full border px-2 py-0.5 font-medium ${tone}`}>{label}</span>
}
